import { Cell } from '@table-library/react-table-library/table';
import numeral from 'numeral';

import { contributorColumns, evidenceFieldColumn, evidenceValueColumn } from '../GscTools/columns';
import { formatMetric } from '../GscTools/format';
import { inspectionLink, pagePerformanceLink, stateLabel } from './rows';
import { STATE } from './rule';

/**
 * The columns this report defines on top of the shared GSC tool sets.
 *
 * The metric columns are the shared ones wherever a row is one Search Console returned, so a page
 * row here reads the same as a page row anywhere else; the share, the rule's verdict and the review
 * state are this report's own, and each says what it is a share or a count *of*.
 */

/** A share of retrieved page impressions, or the reason there is none. */
export function formatShare(t, share) {
    if (share === null || share === undefined) return t('tools.cannibalization.share_unavailable');
    return numeral(share).format('0.0%');
}

/** Block 01's method and block 04's interpretation: a field and what this run says about it. */
export function fieldValueColumns(t, fieldLabel, valueLabel) {
    return [
        evidenceFieldColumn(fieldLabel, { cellSize: 'minmax(190px, 1fr)' }),
        evidenceValueColumn(t, { label: valueLabel, cellSize: 'minmax(320px, 2.6fr)' }),
    ];
}

/**
 * The selection toggle at the end of each shortlist row.
 *
 * A button rather than the table's own checkbox: the selection is the report's, held to a cap the
 * table knows nothing about, and a button can say why it is unavailable when the cap is reached.
 * Left out of the exports — a control is not a value.
 */
function selectColumn(t, { selected, full, onToggle }) {
    return {
        label: t('tools.cannibalization.shortlist.select'),
        key: 'select',
        data: 'key',
        sortType: (array) => array,
        cellSize: 'minmax(130px, 150px)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '';

            const picked = selected.includes(item.key);

            return (
                <div className="d-flex">
                    <button
                        type="button"
                        className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                        aria-pressed={picked}
                        disabled={!picked && full}
                        title={!picked && full ? t('tools.cannibalization.shortlist.full') : ''}
                        onClick={() => onToggle(item.key)}
                    >
                        {picked && <i className="icon-check-icon" aria-hidden="true" />}
                        {picked
                            ? t('tools.cannibalization.shortlist.selected')
                            : t('tools.cannibalization.shortlist.select')}
                    </button>
                </div>
            );
        },
    };
}

/** Block 01's shortlist: the shared query columns plus the selection toggle. */
export function shortlistColumns(t, { hasPosition, selected, full, onToggle }) {
    const shared = contributorColumns(t, {
        hasPosition,
        keyLabel: t('tools.gsc.columns.query'),
        isUrl: false,
    });

    return { shared, table: [...shared, selectColumn(t, { selected, full, onToggle })] };
}

/** A count from a query's evaluation, or the statement that the query has not been evaluated. */
const countCell = (t, field) =>
    function EvaluationCountCell({ item, csvDownload }) {
        const value = item[field];
        if (value === null || value === undefined) {
            const label = t('tools.cannibalization.not_evaluated');
            return csvDownload ? label : <span className="text-muted">{label}</span>;
        }
        return formatMetric('clicks', value);
    };

/** Block 02 — every selected query and what its retrieved pages say under the rule. */
export function candidateColumns(t) {
    const sortByNumber = (field) => (array) =>
        array.sort((a, b) => (a[field] ?? -Infinity) - (b[field] ?? -Infinity));

    return [
        {
            label: t('tools.gsc.columns.query'),
            key: 'key',
            data: 'key',
            sortType: 'string',
            cellSize: 'minmax(220px, 2fr)',
            Component: Cell,
        },
        {
            label: t('tools.cannibalization.candidates.retrieved_urls'),
            key: 'retrievedUrls',
            data: 'retrievedUrls',
            sortType: sortByNumber('retrievedUrls'),
            cellSize: 'minmax(120px, 1fr)',
            Component: Cell,
            renderCell: countCell(t, 'retrievedUrls'),
        },
        {
            label: t('tools.cannibalization.candidates.qualifying_urls'),
            key: 'qualifyingUrls',
            data: 'qualifyingUrls',
            sortType: sortByNumber('qualifyingUrls'),
            cellSize: 'minmax(120px, 1fr)',
            Component: Cell,
            renderCell: countCell(t, 'qualifyingUrls'),
        },
        {
            label: t('tools.cannibalization.candidates.page_impressions'),
            key: 'impressions',
            data: 'impressions',
            sortType: sortByNumber('impressions'),
            cellSize: 'minmax(150px, 1.1fr)',
            Component: Cell,
            renderCell: countCell(t, 'impressions'),
        },
        {
            label: t('tools.cannibalization.candidates.largest_share'),
            key: 'largestShare',
            data: 'largestShare',
            sortType: sortByNumber('largestShare'),
            cellSize: 'minmax(130px, 1fr)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                if (item.retrievedUrls === null) {
                    const label = t('tools.cannibalization.not_evaluated');
                    return csvDownload ? label : <span className="text-muted">{label}</span>;
                }
                return formatShare(t, item.largestShare);
            },
        },
        {
            label: t('tools.cannibalization.candidates.state'),
            key: 'state',
            data: 'state',
            sortType: 'string',
            cellSize: 'minmax(220px, 2fr)',
            Component: Cell,
            renderCell: ({ item, csvDownload }) => {
                const label = stateLabel(t, item);
                if (csvDownload || item.state !== STATE.OVERLAP) return label;
                return <strong>{label}</strong>;
            },
        },
    ];
}

/** The button that opens a candidate's page evidence in block 03. Left out of the exports. */
export function evidenceActionColumn(t, { activeQuery, onOpen }) {
    return {
        label: t('tools.cannibalization.candidates.evidence'),
        key: 'evidence',
        data: 'key',
        sortType: (array) => array,
        cellSize: 'minmax(130px, 150px)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '';

            const picked = item.key === activeQuery;
            const available = item.retrievedUrls !== null;

            return (
                <div className="d-flex">
                    <button
                        type="button"
                        className={`table-action-main-btn ${picked ? 'is-picked' : ''}`}
                        aria-pressed={picked}
                        disabled={!available}
                        onClick={() => onOpen(item.key)}
                    >
                        {picked && <i className="icon-check-icon" aria-hidden="true" />}
                        {picked
                            ? t('tools.cannibalization.candidates.showing')
                            : t('tools.cannibalization.candidates.show')}
                    </button>
                </div>
            );
        },
    };
}

/**
 * Block 03 — the page rows of one query: the shared page columns with the share inserted beside
 * the impressions it is computed from, and whether that share clears the rule.
 */
export function evidenceColumns(t, { hasPosition, rule }) {
    const [url, clicks, impressions, ...rest] = contributorColumns(t, {
        hasPosition,
        keyLabel: t('tools.cannibalization.evidence.url'),
        isUrl: true,
    });

    return [
        url,
        clicks,
        impressions,
        {
            label: t('tools.cannibalization.evidence.share'),
            key: 'share',
            data: 'share',
            sortType: (array) => array.sort((a, b) => (a.share ?? -1) - (b.share ?? -1)),
            cellSize: 'minmax(150px, 1.1fr)',
            Component: Cell,
            renderCell: ({ item }) => formatShare(t, item.share),
        },
        ...rest,
        {
            label: t('tools.cannibalization.evidence.qualifies', {
                share: numeral(rule.minShare).format('0%'),
            }),
            key: 'qualifies',
            data: 'qualifies',
            sortType: (array) => array.sort((a, b) => Number(a.qualifies) - Number(b.qualifies)),
            cellSize: 'minmax(120px, 1fr)',
            Component: Cell,
            renderCell: ({ item }) =>
                item.qualifies
                    ? t('tools.cannibalization.evidence.yes')
                    : t('tools.cannibalization.evidence.no'),
        },
    ];
}

/**
 * The row links: the URL's own performance under this scope, or URL Inspection filled in but not
 * run. Links, not actions — nothing here redirects, canonicalises or merges anything.
 */
export function evidenceLinkColumn(t, scope) {
    const link = (href, label, title) => (
        <a
            key={label}
            className="table-action-main-btn"
            href={href}
            target="_blank"
            rel="noreferrer"
            title={title}
        >
            {label}
        </a>
    );

    return {
        label: t('tools.cannibalization.evidence.open_in'),
        key: 'links',
        data: 'key',
        sortType: (array) => array,
        cellSize: 'minmax(200px, 220px)',
        Component: Cell,
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return '';

            return (
                <div className="gsc-tool-row-links">
                    {link(
                        pagePerformanceLink(scope, item.key),
                        t('tools.cannibalization.evidence.performance'),
                        t('tools.cannibalization.evidence.performance_title'),
                    )}
                    {link(
                        inspectionLink(scope, item.key),
                        t('tools.cannibalization.evidence.inspect'),
                        t('tools.cannibalization.evidence.inspect_title'),
                    )}
                </div>
            );
        },
    };
}
